import { queryOptions } from "@tanstack/react-query";
import { getKeyOnlyQueryConfig } from "@/shared/lib/key-only-query-config";
import {
  fetchLinkedAnalyses,
  fetchLinkedAnalysisSteps,
  fetchTemplateById,
  fetchTemplates,
  fetchTemplateSections,
} from "@/domains/templates/api";
import { toUiDetail, toUiItem, toUiSection } from "@/domains/templates/mappers";
import type {
  NormalizedTemplateFilterOptions,
  TemplateFilterOptions,
} from "@/domains/templates/types";

function normalizeTemplateFilterOptions(
  options: TemplateFilterOptions,
): NormalizedTemplateFilterOptions {
  return {
    search: options.search?.trim() ?? "",
    showGlobal: options.showGlobal === true,
  };
}

const createReportTemplateKeys = {
  all: getKeyOnlyQueryConfig(["reports", "create-report", "templates"]),
  lists: () =>
    getKeyOnlyQueryConfig(["reports", "create-report", "templates", "list"]),
  details: () =>
    getKeyOnlyQueryConfig(["reports", "create-report", "templates", "detail"]),
  allSteps: () =>
    getKeyOnlyQueryConfig(["reports", "create-report", "templates", "steps"]),
  linkedAnalyses: () =>
    getKeyOnlyQueryConfig([
      "reports",
      "create-report",
      "templates",
      "linkedAnalyses",
    ]),
  linkedAnalysisSteps: () =>
    getKeyOnlyQueryConfig([
      "reports",
      "create-report",
      "templates",
      "linkedAnalysisSteps",
    ]),
};

export const createReportTemplateQueries = {
  all: createReportTemplateKeys.all,

  lists: createReportTemplateKeys.lists,

  list: (options: TemplateFilterOptions, currentTenantId?: string) => {
    const normalizedOptions = normalizeTemplateFilterOptions(options);
    return queryOptions({
      queryKey: [
        ...createReportTemplateQueries.lists().queryKey,
        normalizedOptions,
        currentTenantId,
      ],
      queryFn: () =>
        fetchTemplates(normalizedOptions, currentTenantId as string),
      select: (data) => data.map(toUiItem),
      enabled: Boolean(currentTenantId),
    });
  },

  details: createReportTemplateKeys.details,

  detail: (id: string) =>
    queryOptions({
      queryKey: [...createReportTemplateQueries.details().queryKey, id],
      queryFn: () => fetchTemplateById(id),
      select: (row) => toUiDetail(row),
      enabled: Boolean(id),
    }),

  allSteps: createReportTemplateKeys.allSteps,

  steps: (templateId: string) =>
    queryOptions({
      queryKey: [
        ...createReportTemplateQueries.allSteps().queryKey,
        templateId,
      ],
      queryFn: () => fetchTemplateSections(templateId),
      select: (rows) => rows.map(toUiSection),
      enabled: Boolean(templateId),
    }),

  linkedAnalyses: () =>
    queryOptions({
      queryKey: createReportTemplateKeys.linkedAnalyses().queryKey,
      queryFn: fetchLinkedAnalyses,
    }),

  linkedAnalysisSteps: (analysisId: string) =>
    queryOptions({
      queryKey: [
        ...createReportTemplateKeys.linkedAnalysisSteps().queryKey,
        analysisId,
      ],
      queryFn: () => fetchLinkedAnalysisSteps(analysisId),
      enabled: Boolean(analysisId),
    }),
};
